import { redirect } from '@tanstack/react-router'
import { createClient } from '@/utils/supabase/client'
import { getCachedUser } from '@/lib/auth-cache'

// Public routes — historical source: src/utils/supabase/middleware.ts (removed
// in Phase 4). Anything not matched here requires a Supabase session;
// otherwise the user is bounced to /login with `redirect=<original-href>` so
// post-login lands them back where they tried to go.
const PUBLIC_PREFIXES = [
  '/login',
  '/register',
  '/agb',
  '/impressum',
  '/datenschutz',
  '/rechtliches',
  '/setup-password',
  '/reset-password',
  '/confirm-email',
  '/ticket-submit',
  // Subcontractor board access. Capability URL, no account by design — see
  // server/routes/board-public.ts for the token's expiry and revocation.
  '/board-link',
  // The checkout lead form is for prospects who have no account yet — that is
  // its entire purpose. It was never listed here, so every anonymous visitor
  // was bounced to /login and the page has been unreachable for the audience it
  // was written for. Covers /checkout/success too, which Stripe redirects to
  // and which likewise must work without a session.
  '/checkout',
  '/slides',
  '/anleitung',
  '/anleitung-img',
]

function isPublic(pathname: string): boolean {
  if (pathname === '/') return false
  if (pathname.includes('.') && !pathname.includes('/api/')) return false
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

function redirectToLogin(pathname: string): never {
  throw redirect({ to: '/login', search: { redirect: pathname } as { redirect: string } })
}

// Apply the `needs_password_change` gate from whatever user metadata we have
// (live session or the offline cache). Throws a redirect or returns normally.
function enforcePasswordSetup(
  metadata: Record<string, unknown> | undefined,
  pathname: string,
): void {
  if (metadata?.needs_password_change && pathname !== '/setup-password') {
    throw redirect({ to: '/setup-password' as string })
  }
}

// Router `beforeLoad` guard. Runs on EVERY navigation, so it must never block
// on the network: `supabase.auth.getSession()` triggers a token refresh that
// hangs offline (see AuthProvider.tsx — same lesson, same fix).
//
// The previous version only skipped getSession() when `navigator.onLine` was
// false — but that flag lies on iOS WKWebView, reporting `true` on a
// connected-but-no-internet network (site wifi with no uplink, cellular
// handover). So for a signed-in user every tap fell through to getSession(),
// whose refresh hung, and the 2s race timeout fired on *each* navigation → a
// multi-second stall before the page switched.
//
// Fix: decide from the cached user whenever we have one, regardless of the
// (unreliable) online flag. AuthProvider's background getUser() + server RLS
// are the real session gate, and AuthProvider clears the cache the instant a
// session is signed out or revoked (AuthProvider.tsx) — so a cached user here
// means a live identity. getSession() is now only consulted on a cold start
// with no cached identity at all.
export async function requireAuth(pathname: string): Promise<void> {
  if (isPublic(pathname)) return

  const cached = getCachedUser()
  if (cached) {
    enforcePasswordSetup(cached.user.user_metadata, pathname)
    return
  }

  // No cached identity (never signed in on this device, or cache cleared).
  // Offline with nothing to validate against → bounce to login.
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    redirectToLogin(pathname)
  }

  const supabase = createClient()
  // getSession reads from local storage but may try to refresh the token (a
  // network call). Race it against 2s so a slow/stalled refresh can't freeze
  // the first navigation of a cold start.
  const sessionResult = await Promise.race([
    supabase.auth.getSession(),
    new Promise<Awaited<ReturnType<typeof supabase.auth.getSession>> | null>(
      (r) => setTimeout(() => r(null), 2000),
    ),
  ])

  // Timed out with no cache to fall back on — let the navigation through
  // (AuthProvider + server RLS are the real gate, and a false bounce to /login
  // is worse than a deferred check).
  if (sessionResult === null) return

  const { data, error } = sessionResult
  if (error || !data.session) {
    redirectToLogin(pathname)
  }
  enforcePasswordSetup(data.session.user.user_metadata, pathname)
}
